import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { en } from '@/i18n/en';
import { strings } from '@/strings';

export type Language = 'ar' | 'en';
export const LANGUAGE_KEY = 'areeb-language';

type LanguageValue = {
  language: Language;
  isRTL: boolean;
  setLanguage: (next: Language) => void;
  /** A string in the active language, falling back to Arabic when untranslated. */
  t: (key: keyof typeof strings) => string;
};

const LanguageContext = createContext<LanguageValue | null>(null);

export function useLanguage(): LanguageValue {
  const ctx = useContext(LanguageContext);
  // Arabic, not a thrown error: a component rendered outside the provider in a
  // test should show the project's own language, not crash the tree.
  if (!ctx) {
    return { language: 'ar', isRTL: true, setLanguage: () => {}, t: (key) => strings[key] };
  }
  return ctx;
}

function readStored(): Language {
  try {
    return localStorage.getItem(LANGUAGE_KEY) === 'en' ? 'en' : 'ar';
  } catch {
    return 'ar';
  }
}

/**
 * The active language, and the document direction that follows from it.
 *
 * Arabic is the default and the fallback everywhere. English covers the shell
 * only — see i18n/en.ts — so `t` returns the Arabic string for anything not
 * translated, which keeps an untranslated screen usable instead of filling it
 * with key names.
 *
 * `dir` is set on <html> here rather than hard-coded in index.html, because it
 * has to follow the choice. Everything in the shell uses Tailwind's rtl:/ltr:
 * variants, which read that attribute — so the sidebar changes edge and the
 * menus change corner without a single JS branch.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(readStored);
  const isRTL = language === 'ar';

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  }, [language, isRTL]);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    try {
      localStorage.setItem(LANGUAGE_KEY, next);
    } catch {
      // The choice is lost for this browser; the session still works.
    }
  }, []);

  const t = useCallback(
    (key: keyof typeof strings) => (language === 'en' ? en[key] ?? strings[key] : strings[key]),
    [language]
  );

  const value = useMemo(() => ({ language, isRTL, setLanguage, t }), [language, isRTL, setLanguage, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
